import { useList } from "@refinedev/core";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { formatRelativeTime } from "@/lib/formatters";

interface Periodo {
  id: string;
  numero_correlativo: number;
  estado: "cerrado_tienda" | "liquidado" | "pagado" | "anulado";
  cantidad_ventas: number;
  valor_final_transferencia: number;
  cerrado_en: string | null;
  liquidado_en: string | null;
  pagado_en: string | null;
  tiendas: { nombre: string; marca: string } | null;
}

export function KanbanPage() {
  const { data, isLoading } = useList<Periodo>({
    resource: "periodos_cobro",
    filters: [
      {
        field: "estado",
        operator: "in",
        value: ["cerrado_tienda", "liquidado", "pagado", "anulado"],
      },
    ],
    meta: { select: "*, tiendas(nombre, marca)" },
    pagination: { mode: "off" },
  });

  const cols = {
    por_liquidar: data?.data.filter((p) => p.estado === "cerrado_tienda") ?? [],
    liquidadas: data?.data.filter((p) => p.estado === "liquidado") ?? [],
    pagadas: data?.data.filter((p) => p.estado === "pagado") ?? [],
    anuladas: data?.data.filter((p) => p.estado === "anulado") ?? [],
  };

  return (
    <div>
      <PageHeader title="Kanban de cortes" subtitle="Vista por estado" />
      {isLoading ? (
        <div className="card p-8 text-center text-fg-muted">Cargando...</div>
      ) : (
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-4">
          <Columna titulo="Por liquidar" color="state-cerrado" periodos={cols.por_liquidar} accion="liquidar" />
          <Columna titulo="Liquidadas" color="state-liquidado" periodos={cols.liquidadas} accion="pagar" />
          <Columna titulo="Pagadas" color="state-pagado" periodos={cols.pagadas} />
          <Columna titulo="Anuladas" color="state-anulado" periodos={cols.anuladas} />
        </div>
      )}
    </div>
  );
}

function Columna({
  titulo,
  color,
  periodos,
  accion,
}: {
  titulo: string;
  color: string;
  periodos: Periodo[];
  accion?: "liquidar" | "pagar";
}) {
  return (
    <div className="space-y-2">
      <header className="flex items-center justify-between">
        <h2 className="font-semibold text-sm uppercase tracking-wide">{titulo}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-pill bg-${color}/15 text-${color}`}>
          {periodos.length}
        </span>
      </header>
      <ul className="space-y-2">
        {periodos.map((p) => (
          <li key={p.id}>
            <Link
              to={
                accion === "liquidar"
                  ? `/admin/periodos/${p.id}/liquidar`
                  : `/cuentas-cobro/${p.id}`
              }
              className="card p-3 block hover:shadow-md transition-shadow"
            >
              <p className="text-sm font-medium truncate">{p.tiendas?.nombre}</p>
              <p className="text-xs text-fg-muted">
                Corte #{p.numero_correlativo} · {p.cantidad_ventas} ventas
              </p>
              <MoneyDisplay value={p.valor_final_transferencia} className="text-sm mt-1" />
              <p className="text-xs text-fg-muted mt-1">
                {p.estado === "cerrado_tienda" && p.cerrado_en
                  ? `Cerrado ${formatRelativeTime(p.cerrado_en)}`
                  : p.estado === "liquidado" && p.liquidado_en
                    ? `Liquidado ${formatRelativeTime(p.liquidado_en)}`
                    : p.estado === "pagado" && p.pagado_en
                      ? `Pagado ${formatRelativeTime(p.pagado_en)}`
                      : null}
              </p>
            </Link>
          </li>
        ))}
        {periodos.length === 0 && (
          <li className="text-xs text-fg-muted text-center py-4">—</li>
        )}
      </ul>
    </div>
  );
}
